import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { restApi } from "@/store/rest_api";
import RecentPropertySearches from "@/components/pages/properties/RecentPropertySearches";
import { apiErrorMessage } from "@/util/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useCheckDuplicateSearchMutation,
  usePreviewListingsMutation,
  useGetPropertyDataUsageQuery,
  useStartListingsBuildMutation,
  useGetListingsBuildStatusQuery,
} from "@/store/rest_api";
import type PropertySearchCriteria from "@/model/interface/PropertySearchCriteria";
import type {
  PropertyPreviewResult,
  DuplicateSearchWarning,
  PropertyDataUsage,
} from "@/model/interface/PropertyListings";

const PROPERTY_TYPE_OPTIONS = [
  { value: "SINGLE_FAMILY", label: "Single Family" },
  { value: "MULTI_FAMILY", label: "Multi Family" },
  { value: "CONDO", label: "Condo" },
  { value: "TOWNHOUSE", label: "Townhouse" },
  { value: "MOBILE_HOME", label: "Mobile Home" },
  { value: "LAND", label: "Land" },
  { value: "COMMERCIAL", label: "Commercial" },
  { value: "INDUSTRIAL", label: "Industrial" },
  { value: "MIXED_USE", label: "Mixed Use" },
  { value: "OTHER", label: "Other" },
];

const OWNER_TYPE_OPTIONS = [
  { value: "OWNER_OCCUPIED", label: "Owner Occupied" },
  { value: "ABSENTEE", label: "Absentee" },
  { value: "CORPORATE", label: "Corporate" },
  { value: "TRUST", label: "Trust" },
  { value: "UNKNOWN", label: "Unknown" },
];

const NONE = "__none__";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

function buildCostText(leadCount: number, usage?: PropertyDataUsage): string {
  if (!usage) {
    return `Building ${leadCount} ${leadCount === 1 ? "lead" : "leads"}.`;
  }
  const within = Math.min(leadCount, usage.remainingLeads);
  const over = Math.max(0, leadCount - usage.remainingLeads);
  if (over === 0) {
    return `Uses ${within} of your ${usage.remainingLeads} remaining included leads this month.`;
  }
  const overCost = (over * usage.overagePerLeadCents) / 100;
  return `${within} from your included quota, then ${over} over at $${(usage.overagePerLeadCents / 100).toFixed(
    2,
  )}/lead = $${overCost.toFixed(2)} extra.`;
}

export default function RemoteListingsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [criteria, setCriteria] = useState<PropertySearchCriteria>({});
  const [count, setCount] = useState(100);
  const [preview, setPreview] = useState<PropertyPreviewResult | null>(null);
  const [dupWarning, setDupWarning] = useState<DuplicateSearchWarning | null>(null);

  const [buildId, setBuildId] = useState<number | null>(null);

  const [checkDuplicate] = useCheckDuplicateSearchMutation();
  const [previewListings, { isLoading: isPreviewing }] = usePreviewListingsMutation();
  const [startBuild] = useStartListingsBuildMutation();
  const { data: usage } = useGetPropertyDataUsageQuery();
  const { data: build } = useGetListingsBuildStatusQuery(buildId ?? 0, {
    skip: buildId == null,
    pollingInterval: 2500,
  });

  useEffect(() => {
    if (!build) {
      return;
    }
    if (build.status === "READY") {
      toast.success(`Pool built: ${build.totalRows.toLocaleString()} leads ready to import below`);
      dispatch(restApi.util.invalidateTags(["PropertyDataSearches"]));
      setBuildId(null);
    } else if (build.status === "FAILED") {
      toast.error("Build failed");
      setBuildId(null);
    }
  }, [build?.status]);

  const update = (patch: Partial<PropertySearchCriteria>) => {
    setCriteria((prev) => ({ ...prev, ...patch }));
  };

  const handleBuild = async () => {
    const targetCount = preview ? Math.min(count, preview.buildableCount || count) : count;
    try {
      const result = await startBuild({ criteria, count: targetCount }).unwrap();
      setBuildId(result.id);
      toast.message("Building your list...");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not start build"));
    }
  };

  const numericOrUndefined = (raw: string): number | undefined => {
    const parsed = Number(raw);
    return raw === "" || isNaN(parsed) ? undefined : parsed;
  };

  const runPreview = async () => {
    try {
      const result = await previewListings({ criteria, count }).unwrap();
      setPreview(result);
    } catch {
      toast.error("Preview failed");
    }
  };

  const handleSearch = async () => {
    setPreview(null);
    try {
      const dup = await checkDuplicate(criteria).unwrap();
      if (dup.duplicate) {
        setDupWarning(dup);
        return;
      }
    } catch {
      // dedup check is best-effort; proceed to preview
    }
    runPreview();
  };

  const buildCount = preview ? Math.min(count, preview.buildableCount || count) : count;

  return (
    <div className="container p-4 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/properties")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Remote Listings</h1>
          <p className="text-sm text-muted-foreground">
            Build a list of leads (property + owner contacts) from external data providers.
          </p>
        </div>
      </div>

      {usage && (
        <div className="rounded-md border bg-muted/40 px-4 py-2 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-medium">Property Data this month</span>
          <span className="text-muted-foreground">
            {usage.usedLeads} / {usage.includedLeads} leads used
          </span>
          <span>
            <span className="font-medium">{usage.remainingLeads}</span>{" "}
            <span className="text-muted-foreground">remaining</span>
          </span>
          <span className="text-muted-foreground">then ${(usage.overagePerLeadCents / 100).toFixed(2)}/lead</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">State</label>
          <Select value={criteria.state ?? NONE} onValueChange={(value) => update({ state: value === NONE ? undefined : value })}>
            <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Any</SelectItem>
              {US_STATES.map((stateCode) => (
                <SelectItem key={stateCode} value={stateCode}>{stateCode}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">City</label>
          <Input placeholder="Any" value={criteria.city ?? ""} onChange={(event) => update({ city: event.target.value || undefined })} />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">County</label>
          <Input placeholder="Any" value={criteria.county ?? ""} onChange={(event) => update({ county: event.target.value || undefined })} />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">ZIP</label>
          <Input placeholder="Any" value={criteria.zip ?? ""} onChange={(event) => update({ zip: event.target.value || undefined })} />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Property Type</label>
          <Select value={criteria.propertyType ?? NONE} onValueChange={(value) => update({ propertyType: value === NONE ? undefined : value })}>
            <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Any</SelectItem>
              {PROPERTY_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Owner Type</label>
          <Select value={criteria.ownerType ?? NONE} onValueChange={(value) => update({ ownerType: value === NONE ? undefined : value })}>
            <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Any</SelectItem>
              {OWNER_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Market Value (min–max)</label>
          <div className="flex gap-1">
            <Input type="number" placeholder="Min" value={criteria.minMarketValue ?? ""} onChange={(event) => update({ minMarketValue: numericOrUndefined(event.target.value) })} />
            <Input type="number" placeholder="Max" value={criteria.maxMarketValue ?? ""} onChange={(event) => update({ maxMarketValue: numericOrUndefined(event.target.value) })} />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Equity (min–max)</label>
          <div className="flex gap-1">
            <Input type="number" placeholder="Min" value={criteria.minEquity ?? ""} onChange={(event) => update({ minEquity: numericOrUndefined(event.target.value) })} />
            <Input type="number" placeholder="Max" value={criteria.maxEquity ?? ""} onChange={(event) => update({ maxEquity: numericOrUndefined(event.target.value) })} />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="build-count">Leads to build</Label>
          <Input
            id="build-count"
            type="number"
            min={1}
            max={25000}
            value={count}
            onChange={(event) => setCount(Math.max(1, parseInt(event.target.value || "1", 10)))}
          />
        </div>

        <div className="flex items-end">
          <Button onClick={handleSearch} disabled={isPreviewing} className="w-full">
            {isPreviewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            Search
          </Button>
        </div>
      </div>

      {preview && (
        <div className="rounded-md border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold">{preview.matchCount.toLocaleString()}</span>
            <span className="text-muted-foreground">properties match</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Build {buildCount.toLocaleString()} {buildCount === 1 ? "lead" : "leads"}. {buildCostText(buildCount, usage)}
          </p>
          <Button onClick={handleBuild} disabled={buildId != null}>
            {buildId != null ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Building...
              </>
            ) : (
              `Build pool of ${buildCount.toLocaleString()}`
            )}
          </Button>
        </div>
      )}

      <RecentPropertySearches />

      <AlertDialog open={dupWarning !== null} onOpenChange={(open) => !open && setDupWarning(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Similar search recently</AlertDialogTitle>
            <AlertDialogDescription>
              You ran a similar search for this area
              {dupWarning?.lastSearchedAt ? ` on ${new Date(dupWarning.lastSearchedAt).toLocaleString()}` : ""}
              {dupWarning?.importedCount != null ? `, importing ${dupWarning.importedCount} leads` : ""}. Building again
              will spend more credits. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDupWarning(null);
                runPreview();
              }}
            >
              Search anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
